import React, { createContext, useContext, useState, useEffect } from "react";
import logger from "./utils/logger";
import api from "./axiosInstance";
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);
const forge = require('node-forge');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authStatus, setAuthStatus] = useState('checking'); // 'checking' | 'authenticated' | 'unauthenticated'
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginType, setLoginType] = useState('doctor');
  const [clinicId, setClinicId] = useState(null);

  const loginEndpoint = (type) => type === 'staff' ? '/auth/staff/login' : '/auth/doctors/login';

  const login = async (username, password, type = 'doctor') => {
    if (isLoggingIn) {
      return;
    }
    setIsLoggingIn(true);
    try {
      const publicKeyResponse = await api.get('/auth/publicKey');
      const publicKeyPem = publicKeyResponse.data.publicKey;
      const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
      const encrypted = forge.util.encode64(publicKey.encrypt(password, 'RSA-OAEP'));

      const body = {
        username: username,
        password: encrypted
      };

      const response = await api.post(loginEndpoint(type), body);
      const data = response.data;
      const loggedInUser = type === 'staff' ? data.staff : data.doctor;
      setUser(loggedInUser);
      setLoginType(type);
      setAuthStatus('authenticated');
      if (loggedInUser?.clinics?.length) {
        const firstClinicId = loggedInUser.clinics[0];
        setClinicId(firstClinicId);
        localStorage.setItem('selectedClinic', firstClinicId);
      }
    } catch (e) {
      if (e.response?.status === 403 && e.response.data?.code === 'TERMS_NOT_ACCEPTED') {
        return {
          requiresTerms: true,
          pendingToken: e.response.data.pendingToken,
          contractUrl: e.response.data.contractUrl,
          contractVersion: e.response.data.contractVersion,
        };
      }
      logger.error(`Login error: ${e.message}`);
      throw e;
    } finally {
      setIsLoggingIn(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      try {
        const response = await api.get('/auth/me');
        if (cancelled) return;
        const { type, clinicId: meClinicId, doctor, staff } = response.data;
        const isStaffUser = type === 'Staff';
        setUser(isStaffUser ? staff : doctor);
        setLoginType(isStaffUser ? 'staff' : 'doctor');
        setClinicId(meClinicId ?? null);
        setAuthStatus('authenticated');
      } catch (e) {
        if (cancelled) return;
        setUser(null);
        setAuthStatus('unauthenticated');
      }
    };
    bootstrap();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      setAuthStatus('unauthenticated');
    };
    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, []);

  // Fetch a fresh short-lived signed URL for the contract PDF. Used when the
  // URL from login expired while the doctor was reading. Returns
  // { contractVersion, contractUrl }. A 401 here means the pendingToken itself
  // expired — the caller should send the doctor back to login.
  const refreshContract = async (pendingToken) => {
    const response = await api.post('/auth/doctors/contract', { pendingToken });
    return response.data;
  };

  const acceptTerms = async (pendingToken) => {
    const response = await api.post('/auth/doctors/accept-terms', { pendingToken });
    const { doctor } = response.data;
    setUser(doctor);
    setLoginType('doctor');
    setAuthStatus('authenticated');
    if (doctor?.clinics?.length) {
      const firstClinicId = doctor.clinics[0];
      setClinicId(firstClinicId);
      localStorage.setItem('selectedClinic', firstClinicId);
    }
  };

  const switchClinic = async (newClinicId) => {
    const response = await api.post('/auth/switch-clinic', { clinicId: newClinicId });
    const loggedInUser = response.data.doctor ?? response.data.staff;
    setUser(loggedInUser);
    setClinicId(newClinicId);
    localStorage.setItem('selectedClinic', newClinicId);
    return loggedInUser;
  };

  const forgotPassword = async (oldPassword, newPassword, userType, identifier) => {
    try {
      const publicKeyResponse = await api.get('/auth/publicKey');
      const publicKeyPem = publicKeyResponse.data.publicKey;

      const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
      const encryptedOldPassword = forge.util.encode64(publicKey.encrypt(oldPassword, 'RSA-OAEP'));
      const encryptedNewPassword = forge.util.encode64(publicKey.encrypt(newPassword, 'RSA-OAEP'));

      await api.post('/auth/changePassword', {}, {
        headers: {
          "x-old-password": encryptedOldPassword,
          "x-new-password": encryptedNewPassword,
          userType,
          identifier
        }
      });
      alert("Password changed successfully");
    } catch (error) {
      alert(`Change password error: ${error.response?.data?.message || error.message}`);
    }
  };

  const logout = () => {
    setUser(null);
    setAuthStatus('unauthenticated');
    setLoginType('doctor');
    setClinicId(null);
    localStorage.removeItem('selectedClinic');
    api.post('/auth/logout').catch((e) => logger.error('Logout error:', e.message));
  };

  const value = {
    user,
    login,
    acceptTerms,
    refreshContract,
    logout,
    isLoggingIn,
    forgotPassword,
    setUser,
    switchClinic,
    authStatus,
    loginType,
    clinicId,
    isStaff: loginType === 'staff'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
